import { z } from 'zod';
/**
 * SIGNING EVIDENCE — the IP address and user agent behind one signature, kept OUT of the audit
 * trail (ADR-014 §3, #215).
 *
 * WHY IT IS NOT AN AUDIT ROW. Audit rows carry no PII by rule, are append-only, and are kept for
 * the life of the system. An IP address is personal data, so putting it in the trail would make
 * the trail a second personal-data store with none of the record's retention controls. The audit
 * row for a signing is `step.signed`, subject `step-instance`, carrying this record's id in
 * `reason` — ids only.
 *
 * WHAT IT IS FOR. Attribution of an electronic signature (ESIGN; Virginia UETA, Va. Code
 * § 59.1-479 et seq.) can be shown "in any manner". The primary proof is the security procedure —
 * the single-person link, the access code, the session, the typed name. IP and user agent are
 * CORROBORATING. The legacy portal recorded them and surveyors ask for them; neither is relied on
 * alone, which is why `ip` is nullable and nothing refuses a signature that lacks one.
 */
/**
 * The client as the platform reported it.
 *
 * `ipHeader` RECORDS ITS OWN PROVENANCE, and that is deliberate. Static Web Apps proxies `/api` to
 * the linked Function App, and Microsoft does not document which forwarded header carries the
 * client address — ADR-014 says so and makes verifying it the first task of this ticket. Until
 * that observation is made on staging, every stored record still says which header its value came
 * from, so the question can be settled against real evidence later rather than being invisible in
 * the data. A value is labelled "as reported", never "verified".
 */
export const signatureClientSchema = z
    .object({
    /** Best-effort client address AS REPORTED by the platform. Null when no header carried one. */
    ip: z.string().min(1).max(64).nullable(),
    /**
     * Which header the address came from, or `'none'` when none did. Never empty: a record whose
     * provenance is blank is the one case this field exists to prevent.
     */
    ipHeader: z.string().min(1).max(64),
    /**
     * The RAW header value, truncated to 256. Kept because `X-Forwarded-For` is appended to by
     * every proxy and can be set by the client, so the single address above is a reading of this
     * chain rather than a fact — whoever audits a disputed signature needs what was actually sent.
     */
    forwardedChain: z.string().max(256),
    userAgent: z.string().max(512).nullable(),
})
    .strict();
/** The opaque ids the audit trail already uses, so evidence and trail can be lined up without PII. */
export const signatureLinkSchema = z
    .object({ tokenHashPrefix: z.string().min(1).max(64), sessionIdPrefix: z.string().min(1).max(64) })
    .strict();
export const signatureEvidenceSchema = z
    .object({
    id: z.string().min(1),
    personId: z.string().min(1),
    instanceId: z.string().min(1),
    attempt: z.number().int().nonnegative(),
    /** What was signed, pinned: the definition the instance carried at submit. */
    definition: z.object({ id: z.string().min(1), version: z.number().int().nonnegative(), contentHash: z.string().min(1) }).strict(),
    /** pdf-form only: the template the answers were placed on. */
    template: z.object({ templateId: z.string().min(1), version: z.number().int().nonnegative(), sourceHash: z.string().min(1) }).strict().optional(),
    /** SERVER time at submit, deliberately separate from the date printed on the form. */
    signedAt: z.string().datetime(),
    /** Exactly as typed; also in the submission, because this record must stand on its own. */
    typedName: z.string().min(1).max(200),
    /** The acknowledgment wording's hash, where the step has one. */
    acceptedTextHash: z.string().min(1).optional(),
    /** The flattened PDF or rendered record, where one was produced. */
    artifactSha256: z.string().min(1).optional(),
    link: signatureLinkSchema,
    client: signatureClientSchema,
})
    .strict();
/**
 * The document id. One per ATTEMPT, not per instance: a step that was sent back and signed again
 * produces a second signature, and overwriting the first would destroy the evidence for a
 * signature that really happened.
 */
export const signatureEvidenceId = (instanceId, attempt) => `signature:${instanceId}:${attempt}`;
/** The header the platform's own edge sets, preferred over anything a client can write. */
export const EDGE_CLIENT_IP_HEADER = 'x-azure-clientip';
export const FORWARDED_FOR_HEADER = 'x-forwarded-for';
export const MAX_USER_AGENT = 512;
/**
 * The longest thing that can still be an address. An IPv6 address with a zone and brackets is
 * comfortably under this; anything longer is not an address somebody sent, it is a header somebody
 * stuffed. Caps the field rather than letting an over-long value reach the schema, which would
 * refuse the whole record — and a request that writes no evidence because its header was too long
 * is a signature with no corroboration, caused by the attacker who sent the header.
 */
export const MAX_IP = 64;
export const MAX_FORWARDED_CHAIN = 256;
/**
 * Read the client from request headers, by the SAME rule the portal's per-IP rate limit already
 * uses (`clientIpOf`, `apps/api/src/functions/portal-reissue.ts`): the edge header, else the LAST
 * `x-forwarded-for` hop.
 *
 * ONE RULE, NOT TWO. A second derivation would be a second opinion about who a caller is, and the
 * first time the two disagreed — the limiter counting one address while the evidence recorded
 * another — neither record would be trustworthy. The last hop rather than the first because
 * `X-Forwarded-For` is appended to by each proxy, so the earlier entries are whatever the client
 * chose to send.
 *
 * Takes a plain lookup rather than a request, so it is testable without a platform object and
 * usable from either side.
 */
export function signatureClientFrom(header) {
    const edge = (header(EDGE_CLIENT_IP_HEADER) ?? '').trim();
    const chain = (header(FORWARDED_FOR_HEADER) ?? '').trim();
    const lastHop = chain.split(',').map((part) => part.trim()).filter((part) => part !== '').at(-1) ?? '';
    const chosen = edge !== '' ? { ip: edge, ipHeader: EDGE_CLIENT_IP_HEADER } : lastHop !== '' ? { ip: lastHop, ipHeader: FORWARDED_FOR_HEADER } : { ip: null, ipHeader: 'none' };
    const address = chosen.ip === null ? null : stripPort(chosen.ip).toLowerCase() || null;
    // Too long to be an address: record NO address rather than a truncated one, which would be a
    // different address and worse than none. The raw header is still kept in `forwardedChain`, so
    // nothing is lost — and `ipHeader` goes back to 'none', because naming a header we took no
    // usable value from would overstate what this record knows.
    const usable = address !== null && address.length <= MAX_IP ? address : null;
    const userAgent = (header('user-agent') ?? '').trim();
    return {
        ip: usable,
        ipHeader: usable === null ? 'none' : chosen.ipHeader,
        forwardedChain: chain.slice(0, MAX_FORWARDED_CHAIN),
        userAgent: userAgent === '' ? null : userAgent.slice(0, MAX_USER_AGENT),
    };
}
/** `1.2.3.4:5678` is an address with a port; `::1` is an address. Brackets settle the ambiguous case. */
function stripPort(value) {
    const bracketed = /^\[([^\]]+)\](?::\d+)?$/.exec(value);
    if (bracketed?.[1] !== undefined)
        return bracketed[1];
    return value.split(':').length === 2 ? (value.split(':')[0] ?? '') : value;
}
//# sourceMappingURL=signature-evidence.js.map